import React, { Component } from "react";
import {
  View,
  Text,
  Image,
  StyleSheet,
  TouchableOpacity,
  Platform,
  TextInput,
  Animated,
  Easing,
} from "react-native";
import { connect } from "react-redux";
import { MaterialCommunityIcons, FontAwesome } from "@expo/vector-icons";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useNavigation } from "@react-navigation/native";
import Lottie from "lottie-react";
import { BRANDING } from "@env";
import colors from "../../constants/colors";
import { logout } from "../../actions/login";
import { getTasks, setTaskSelected, setFormsFromAPI } from "../../actions/tasks";
import { setHeaderSearch } from "../../actions/menu";
import { getAllRecords, getAllTaskFromLog } from "../../actions/records";
import { confirmationAlert } from "../../utils";

const SEARCH_SCREENS = ["NewTask", "NewLogTask", "RecordsView"];
const SEARCH_BTN_SIZE = 33;
const SEARCH_OPEN_WIDTH = 210;
let _interval = null;

function getInitials(username = "") {
  if (!username) return "?";
  const local = username.includes("@") ? username.split("@")[0] : username;
  const parts = local.replace(/[._-]+/g, " ").split(" ").filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

class IqopsHeaderInner extends Component {
  state = {
    searchOpen: false,
  };

  searchProgress = new Animated.Value(0);
  searchInputRef = React.createRef();

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.currentRoute !== this.props.currentRoute) {
      if (
        this.props.currentRoute === "MainView" ||
        !SEARCH_SCREENS.includes(this.props.currentRoute)
      ) {
        if (this.state.searchOpen) this.setSearchOpen(false);
        else if (this.props.headerSearch) this.props.setHeaderSearch("");
      }
    }
    if (!prevState.searchOpen && this.state.searchOpen) {
      setTimeout(() => this.searchInputRef.current?.focus(), 80);
    }
  }

  confirmLogout = () => {
    const { dispatch, navigation } = this.props;
    confirmationAlert(
      null,
      "Are you sure you want to logout?",
      "OK",
      "Cancel",
      null,
      null,
      null,
      () => {
        dispatch(logout());
        if (Platform.OS === "web") {
          window.location.reload(false);
        } else {
          navigation.navigate("Login");
        }
      }
    );
  };

  openAccountMenu = () => {
    this.confirmLogout();
  };

  refresh = () => {
    const {
      currentRoute,
      tasksStatus,
      getTasks,
      setTaskSelected,
      setFormsFromAPI,
      getAllRecords,
      getAllTaskFromLog,
      isOnline,
    } = this.props;

    if (currentRoute === "NewTask" || currentRoute === "NewLogTask") {
      setFormsFromAPI(this.props.formsFilter);
      return;
    }
    if (currentRoute === "RecordsView") {
      if (isOnline) {
        getAllRecords({ page: 1 });
        getAllTaskFromLog({ page: 1 });
      }
      return;
    }
    if (tasksStatus === "idle") {
      getTasks();
      setTaskSelected({});
    }
  };

  setSearchOpen = (next) => {
    this.setState({ searchOpen: next }, ()=>{
      if(next){
        _interval = setTimeout(() => this.toggleSearch(), 8000);
      }else{
        if(_interval)clearTimeout(_interval);
      }
    });
    Animated.timing(this.searchProgress, {
      toValue: next ? 1 : 0,
      duration: 240,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
    if (!next) this.props.setHeaderSearch("");
  };

  toggleSearch = () => {
    this.setSearchOpen(!this.state.searchOpen);
  };

  onSearchChange = (text) => {
    if(_interval)clearTimeout(_interval);
    if(text.length <= 0){
      _interval = setTimeout(() => this.toggleSearch(), 8000);
    }
    this.props.setHeaderSearch(text);
  };

  getPlaceholderText = () => {
    const { currentRoute } = this.props;
    var placeholderText = "";
    if (currentRoute === "NewTask") {
      placeholderText = "Search a form by name";
    } else if (currentRoute === "NewLogTask") {
      placeholderText = "Search a log by name";
    } else if (currentRoute === "RecordsView") {
      placeholderText = "Search a record by name";
    }
    return placeholderText;
  };

  render() {
    const {
      username,
      companyName,
      tasksStatus,
      formsLoaded,
      formsLoadedEnd,
      recordsLoading,
      insets,
      currentRoute,
      headerSearch,
      showBack,
      title,
      onBack,
    } = this.props;
    if (BRANDING !== "iqops") return null;

    const initials = getInitials(username);
    var refreshing = false;
    if (currentRoute == "MainView") {
      refreshing = tasksStatus === "loading";
    } else if (currentRoute == "RecordsView") {
      refreshing = recordsLoading;
    } else if (currentRoute == "NewTask" || currentRoute == "NewLogTask") {
      // Tied to setFormsFromAPI / getForms2, not login storage formsLoading.
      refreshing = !formsLoaded || !formsLoadedEnd;
    }
    const canSearch = SEARCH_SCREENS.includes(currentRoute);
    const showCompanyName = currentRoute === "MainView" && !!companyName;
    const { searchOpen } = this.state;
    const searchWidth = this.searchProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [SEARCH_BTN_SIZE, SEARCH_OPEN_WIDTH],
    });

    return (
      <View style={[styles.safe, { paddingTop: Math.max(insets?.top || 0, 8) }]}>
        <View style={styles.row}>
          {showBack ? (
            <TouchableOpacity style={styles.backBtn} onPress={onBack} activeOpacity={0.7}>
              <MaterialCommunityIcons name="chevron-left" size={28} color={colors.primary} />
            </TouchableOpacity>
          ) : null}

          <View style={styles.logoWrap}>
            {title ? (
              <Text style={styles.titleText} numberOfLines={1}>
                {title}
              </Text>
            ) : (
              <Image
                source={require("../../../assets/iQOps-_Logo-WhiteBF.png")}
                style={styles.logo}
                resizeMode="contain"
              />
            )}
          </View>

          <View style={styles.centerWrap} pointerEvents="box-none" />

          <View style={styles.actions}>
            {showCompanyName ? (
              <View style={styles.companyWrap}>
                <Text style={styles.companyName} numberOfLines={1}>
                  {companyName}
                </Text>
                <MaterialCommunityIcons
                  name="chevron-down"
                  size={12}
                  color={colors.iqopsCompany}
                  style={styles.companyChevron}
                />
              </View>
            ) : null}

            {canSearch ? (
              <Animated.View style={[styles.searchBar, { width: searchWidth }]}>
                <TextInput
                  ref={this.searchInputRef}
                  value={headerSearch}
                  onChangeText={this.onSearchChange}
                  placeholder={this.getPlaceholderText()}
                  placeholderTextColor={colors.iqopsSearchText}
                  style={[styles.searchInput, !searchOpen && styles.searchInputHidden]}
                  returnKeyType="search"
                  editable={searchOpen}
                  pointerEvents={searchOpen ? "auto" : "none"}
                />
                <TouchableOpacity
                  style={[
                    styles.searchBtn,
                    searchOpen ? styles.searchBtnSelected : styles.searchBtnUnselected,
                  ]}
                  onPress={this.toggleSearch}
                  activeOpacity={0.8}
                >
                  <Image
                    source={
                      searchOpen
                        ? require("../../../assets/Icons-Screens/search-selected.png")
                        : require("../../../assets/Icons-Screens/search-unselected.png")
                    }
                    style={searchOpen ? styles.searchIconImg : styles.searchIconImgDark}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
              </Animated.View>
            ) : null}

            <TouchableOpacity
              style={styles.iconBtn}
              onPress={this.refresh}
              disabled={refreshing}
              activeOpacity={0.7}
            >
              {refreshing ? (
                <Lottie
                  style={styles.refreshLoader}
                  animationData={require("../animations/loading-dots-gray.json")}
                  autoPlay
                  loop
                />
              ) : (
                <FontAwesome name="refresh" size={14} color={colors.iqopsIcon} />
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.avatar}
              onPress={this.openAccountMenu}
              activeOpacity={0.7}
            >
              <Text style={styles.avatarText}>{initials}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  }
}

function IqopsHeader(props) {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();

  const onBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate("MainView");
    }
  };

  return (
    <IqopsHeaderInner
      {...props}
      insets={insets}
      navigation={navigation}
      currentRoute={props.currentRoute || props.route?.name}
      onBack={onBack}
    />
  );
}

const styles = StyleSheet.create({
  safe: {
    backgroundColor: "#ffffff",
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#e6eef8",
  },
  row: {
    minHeight: 52,
    paddingLeft: 16,
    paddingRight: 14,
    paddingBottom: 8,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  backBtn: {
    marginRight: 2,
    marginLeft: -6,
  },
  logoWrap: {
    alignItems: "flex-start",
    justifyContent: "center",
    flexShrink: 0,
    zIndex: 2,
  },
  logo: {
    height: 30,
    width: 112,
  },
  titleText: {
    color: colors.primary,
    fontSize: 16,
    fontWeight: "700",
    maxWidth: 120,
  },
  centerWrap: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 6,
    minWidth: 0,
  },
  companyWrap: {
    flexDirection: "row",
    alignItems: "center",
    maxWidth: 120,
    marginRight: 8,
    flexShrink: 1,
  },
  companyName: {
    color: colors.iqopsCompany,
    fontFamily: "Poppins-Medium",
    fontSize: 10,
    fontWeight: "500",
    flexShrink: 1,
  },
  companyChevron: {
    marginLeft: 1,
  },
  actions: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 0,
    zIndex: 2,
  },
  searchBar: {
    height: SEARCH_BTN_SIZE,
    borderRadius: SEARCH_BTN_SIZE / 2,
    backgroundColor: colors.iqopsSearchBar,
    flexDirection: "row",
    alignItems: "center",
    overflow: "hidden",
    marginRight: 6,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    height: SEARCH_BTN_SIZE,
    fontFamily: "Poppins-MediumItalic",
    fontSize: 13,
    fontStyle: "italic",
    fontWeight: "500",
    color: colors.iqopsSearchText,
    paddingVertical: 0,
    paddingLeft: 12,
    paddingRight: 8,
    borderWidth: 0,
    outlineStyle: "none",
    outlineWidth: 0,
  },
  searchInputHidden: {
    width: 0,
    maxWidth: 0,
    flex: 0,
    minWidth: 0,
    paddingLeft: 0,
    paddingRight: 0,
    opacity: 0,
  },
  searchBtn: {
    width: SEARCH_BTN_SIZE,
    height: SEARCH_BTN_SIZE,
    borderRadius: SEARCH_BTN_SIZE / 2,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  searchBtnUnselected: {
    backgroundColor: colors.iqopsIconCircle,
    backgroundImage: `radial-gradient(circle, ${colors.iqopsIconCircle} 0%, ${colors.iqopsIconCircleEnd} 100%)`,
  },
  searchBtnSelected: {
    backgroundColor: colors.iqopsSearchSelectedBg,
  },
  searchIconImg: {
    width: 16,
    height: 16,
    tintColor: colors.iqopsSearchSelectedIcon,
  },
  searchIconImgDark: {
    width: 16,
    height: 16,
    tintColor: colors.iqopsIcon,
  },
  iconBtn: {
    width: 27,
    height: 27,
    borderRadius: 13.5,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
    backgroundColor: colors.iqopsIconCircle,
    borderWidth: 1,
    borderColor: colors.iqopsIconCircleEnd,
    backgroundImage: `radial-gradient(circle, ${colors.iqopsIconCircle} 0%, ${colors.iqopsIconCircleEnd} 100%)`,
  },
  refreshLoader: {
    width: 14,
    height: 14,
  },
  avatar: {
    width: 27,
    height: 27,
    borderRadius: 13.5,
    backgroundColor: colors.iqopsIconCircle,
    borderWidth: 1,
    borderColor: colors.iqopsIconCircleEnd,
    alignItems: "center",
    justifyContent: "center",
    backgroundImage: `radial-gradient(circle, ${colors.iqopsIconCircle} 0%, ${colors.iqopsIconCircleEnd} 100%)`,
  },
  avatarText: {
    color: colors.iqopsIcon,
    fontSize: 14,
    fontWeight: "700",
  },
});

export default connect(
  (state) => ({
    username: state.user.get("username"),
    companyName: state.user.get("companyName") || "",
    tasksStatus: state.tasks.get("status"),
    formsLoaded: state.tasks.get("formsLoaded"),
    formsLoadedEnd: state.tasks.get("formsLoadedEnd"),
    recordsLoading: state.records.get("recordsLoading"),
    headerSearch: state.user.get("headerSearch") || "",
    formsFilter: state.tasks.get("formsFilter"),
    isOnline: true,
  }),
  (dispatch) => ({
    dispatch,
    getTasks: () => dispatch(getTasks()),
    setTaskSelected: (value) => dispatch(setTaskSelected(value)),
    setFormsFromAPI: (formsFilter) => dispatch(setFormsFromAPI(formsFilter)),
    setHeaderSearch: (value) => dispatch(setHeaderSearch(value)),
    getAllRecords: (params) => dispatch(getAllRecords(params)),
    getAllTaskFromLog: (params) => dispatch(getAllTaskFromLog(params)),
  })
)(IqopsHeader);
